import React, { useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Typography, Chip,
  TextField, Button, Stack, Tabs, Tab, Paper, CircularProgress, Tooltip,
  IconButton, Divider, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.example.com/v1';

const METHOD_COLORS = { GET: 'info', POST: 'success', PUT: 'warning', DELETE: 'error' };

// Tenant inventory data-manipulation endpoints.
const ENDPOINTS = [
  { id: 'list', method: 'GET', path: '/inventory', summary: 'List inventory items',
    query: ['search', 'category', 'limit', 'cursor', 'showInactive', 'showDeleted'] },
  { id: 'create', method: 'POST', path: '/inventory', summary: 'Create an inventory item', body: true,
    sample: { name: 'Widget', sku: 'WIDGET-001', quantity: 100, unit: 'piece', category: 'General' } },
  { id: 'get', method: 'GET', path: '/inventory/{itemId}', summary: 'Get a single item', pathParams: ['itemId'] },
  { id: 'update', method: 'PUT', path: '/inventory/{itemId}', summary: 'Update an item', pathParams: ['itemId'], body: true,
    sample: { name: 'Widget v2', quantity: 120 } },
  { id: 'delete', method: 'DELETE', path: '/inventory/{itemId}', summary: 'Soft-delete an item (7-day window)', pathParams: ['itemId'] },
  { id: 'adjust', method: 'POST', path: '/inventory/{itemId}/adjust', summary: 'Adjust quantity by a delta', pathParams: ['itemId'], body: true,
    sample: { delta: -5, reason: 'Sold' } },
  { id: 'status', method: 'PUT', path: '/inventory/{itemId}/status', summary: 'Change item status', pathParams: ['itemId'], body: true,
    sample: { status: 'inactive' } },
  { id: 'history', method: 'GET', path: '/inventory/{itemId}/history', summary: 'View item change history', pathParams: ['itemId'] },
  { id: 'catList', method: 'GET', path: '/inventory/categories', summary: 'List categories' },
  { id: 'catCreate', method: 'POST', path: '/inventory/categories', summary: 'Create a category', body: true, sample: { name: 'Electronics' } },
  { id: 'catUpdate', method: 'PUT', path: '/inventory/categories/{categoryId}', summary: 'Rename a category', pathParams: ['categoryId'], body: true, sample: { name: 'Gadgets' } },
  { id: 'catDelete', method: 'DELETE', path: '/inventory/categories/{categoryId}', summary: 'Delete a category (items move to Uncategorized)', pathParams: ['categoryId'] },
];

function resolvePath(template, pathParams) {
  return template.replace(/\{(\w+)\}/g, (_, k) => pathParams[k] || `{${k}}`);
}

function minify(jsonStr) {
  if (!jsonStr) { return ''; }
  try { return JSON.stringify(JSON.parse(jsonStr)); } catch { return jsonStr.replace(/\s+/g, ' ').trim(); }
}

function curlSnippet(ep, url, apiKey, body) {
  const lines = [`curl -X ${ep.method} "${url}"`, `  -H "Authorization: Bearer ${apiKey}"`];
  if (ep.body) { lines.push('  -H "Content-Type: application/json"', `  -d '${minify(body) || '{}'}'`); }
  return lines.join(' \\\n');
}

function jsSnippet(ep, url, apiKey, body) {
  const headers = ep.body
    ? `{\n    "Authorization": "Bearer ${apiKey}",\n    "Content-Type": "application/json"\n  }`
    : `{\n    "Authorization": "Bearer ${apiKey}"\n  }`;
  const bodyLine = ep.body ? `,\n  body: JSON.stringify(${minify(body) || '{}'})` : '';
  return `const res = await fetch("${url}", {\n  method: "${ep.method}",\n  headers: ${headers}${bodyLine}\n});\nconst data = await res.json();\nconsole.log(res.status, data);`;
}

function pythonSnippet(ep, url, apiKey, body) {
  const headerLines = ep.body
    ? `headers={\n        "Authorization": "Bearer ${apiKey}",\n        "Content-Type": "application/json",\n    }`
    : `headers={"Authorization": "Bearer ${apiKey}"}`;
  const dataLine = ep.body ? `,\n    data='''${minify(body) || '{}'}'''` : '';
  return `import requests\n\nres = requests.${ep.method.toLowerCase()}(\n    "${url}",\n    ${headerLines}${dataLine},\n)\nprint(res.status_code, res.json())`;
}

function httpieSnippet(ep, url, apiKey, body) {
  const auth = `"Authorization: Bearer ${apiKey}"`;
  if (ep.body) {
    return `echo '${minify(body) || '{}'}' | http ${ep.method} "${url}" ${auth} Content-Type:application/json`;
  }
  return `http ${ep.method} "${url}" ${auth}`;
}

const SNIPPET_TABS = [
  { label: 'cURL', lang: 'bash', gen: curlSnippet },
  { label: 'JavaScript', lang: 'javascript', gen: jsSnippet },
  { label: 'Python', lang: 'python', gen: pythonSnippet },
  { label: 'HTTPie', lang: 'bash', gen: httpieSnippet },
];

function CopyBlock({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="pre" sx={{
        bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontSize: 12.5,
        overflow: 'auto', m: 0, fontFamily: 'monospace', whiteSpace: 'pre',
      }}>
        {text}
      </Box>
      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
        <IconButton size="small" onClick={copy}
          sx={{ position: 'absolute', top: 4, right: 4, color: 'grey.300', '&:hover': { color: 'white' } }}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function EndpointRow({ ep, apiKey }) {
  const [pathParams, setPathParams] = useState({});
  const [queryStr, setQueryStr] = useState('');
  const [body, setBody] = useState(ep.sample ? JSON.stringify(ep.sample, null, 2) : '');
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snipTab, setSnipTab] = useState(0);

  const url = `${API_BASE}${resolvePath(ep.path, pathParams)}${queryStr ? `?${queryStr}` : ''}`;
  const keyForSnippet = apiKey || 'YOUR_API_KEY';

  const send = async () => {
    if (!apiKey) { return; }
    setLoading(true);
    setResp(null);
    try {
      // Authenticate the call with the tenant API key (not the portal session).
      const opts = { method: ep.method, headers: { Authorization: `Bearer ${apiKey}` } };
      if (ep.body && body) { opts.headers['Content-Type'] = 'application/json'; opts.body = body; }
      const res = await fetch(url, opts);
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = text; }
      setResp({ status: res.status, ok: res.ok, data });
    } catch (e) {
      setResp({ status: 0, ok: false, data: { error: e.message } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Accordion disableGutters sx={{ '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
          <Chip label={ep.method} size="small" color={METHOD_COLORS[ep.method]} sx={{ fontWeight: 700, minWidth: 64 }} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{ep.path}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', display: { xs: 'none', sm: 'block' } }}>
            {ep.summary}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {/* Path params */}
          {(ep.pathParams || []).length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {ep.pathParams.map((p) => (
                <TextField key={p} size="small" label={p} value={pathParams[p] || ''}
                  onChange={(e) => setPathParams((s) => ({ ...s, [p]: e.target.value }))} />
              ))}
            </Stack>
          )}

          {/* Query string */}
          {(ep.query || []).length > 0 && (
            <TextField size="small" fullWidth label="Query string"
              placeholder={ep.query.map((q) => `${q}=`).join('&')}
              value={queryStr} onChange={(e) => setQueryStr(e.target.value)}
              helperText={`Available: ${ep.query.join(', ')}`} />
          )}

          {/* Body */}
          {ep.body && (
            <TextField size="small" fullWidth multiline minRows={4} label="Request body (JSON)"
              value={body} onChange={(e) => setBody(e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12.5 } }} />
          )}

          <Box>
            <Button variant="contained" size="small" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              onClick={send} disabled={loading || !apiKey}>
              Send
            </Button>
            {!apiKey && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                Enter an API key above to send a request.
              </Typography>
            )}
          </Box>

          {/* Response */}
          {resp && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Typography variant="caption" fontWeight={700}>Response</Typography>
                <Chip size="small" label={resp.status || 'ERR'} color={resp.ok ? 'success' : 'error'} sx={{ height: 18, fontSize: 10 }} />
              </Stack>
              <Box component="pre" sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontSize: 12, overflow: 'auto', m: 0, maxHeight: 280 }}>
                {typeof resp.data === 'string' ? resp.data : JSON.stringify(resp.data, null, 2)}
              </Box>
            </Box>
          )}

          <Divider />

          {/* Snippets */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              Code snippets (external use with your API key)
            </Typography>
            <Tabs value={snipTab} onChange={(_, v) => setSnipTab(v)} variant="scrollable" scrollButtons="auto"
              sx={{ minHeight: 32, mb: 1, '& .MuiTab-root': { minHeight: 32, py: 0, textTransform: 'none' } }}>
              {SNIPPET_TABS.map((t) => <Tab key={t.label} label={t.label} />)}
            </Tabs>
            <CopyBlock text={SNIPPET_TABS[snipTab].gen(ep, url, keyForSnippet, body)} />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function ApiExplorer() {
  const [apiKey, setApiKey] = useState('');

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 4 }}>
      <Typography variant="h6" fontWeight={700}>API Explorer</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Try the inventory API and copy ready-to-use snippets. Base URL: <code>{API_BASE}</code>
      </Typography>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Paste a tenant API key below. Both <strong>Send</strong> and the <strong>snippets</strong>
        authenticate with it via <code>Authorization: Bearer &lt;key&gt;</code>. Keys are shown only
        once at creation, so use one you saved.
      </Alert>

      <TextField
        size="small" fullWidth label="API key"
        placeholder="inv_live_…  — paste a key you saved at creation"
        value={apiKey} onChange={(e) => setApiKey(e.target.value)}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
        sx={{ mb: 2 }}
      />

      {ENDPOINTS.map((ep) => <EndpointRow key={ep.id} ep={ep} apiKey={apiKey} />)}
    </Paper>
  );
}
